import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   Inject,
   LOCALE_ID,
   OnChanges,
   ViewEncapsulation,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { ReportParameterFormFactoryService } from '@ndt/reports/shared/report-parameter-list/services/report-parameter-form-factory.service';
import { Store } from '@ngrx/store';
import { interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Tiles } from '../../containers/tile-wrapper/tile-component-accessor';
import { TileBase } from '../shared/tile-base';
import { RunClock_runClockTile } from './queries/generated/RunClock';
import { ClockParametersFormService } from './services/clock-parameters-form.service';

@Component({
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ndt-clock-tile',
    styleUrls: ['./clock-tile.component.scss'],
    templateUrl: './clock-tile.component.html',
    standalone: false
})
export class ClockTileComponent
   extends TileBase<ClockParametersFormService, RunClock_runClockTile>
   implements OnChanges
{
   currentTime!: string;

   constructor(
      @Inject(LOCALE_ID) private locale: string,
      protected formServiceFactory: ReportParameterFormFactoryService,
      protected store: Store,
      public changeDetectorRef: ChangeDetectorRef,
      protected fb: UntypedFormBuilder,
      protected router: Router
   ) {
      super(formServiceFactory, store, changeDetectorRef, fb, router);
      this.formService = formServiceFactory.getService(Tiles.Clock);

      interval(100)
         .pipe(takeUntil(this.destroyed$))
         .subscribe(() => {
            this.refresh();
            this.changeDetectorRef.markForCheck();
         });
   }

   ngOnChanges(): void {
      this.refresh();
   }

   getFontSizeClass(): string | undefined {
      const width: number = this.tileConfig?.dashboardTileLayoutConfiguration?.width;

      switch (width) {
         case 1:
            return '';
         case 2:
            return 'clock-font-2';
         case 3:
            return 'clock-font-3';
         default:
            return 'clock-font-max';
      }
   }

   private refresh(): void {
      this.currentTime = this.getCurrentLocalTime(this.locale, this.tileData?.data?.timeZone);

      if (this.currentTime === '') {
         this.currentTime = new Date().toLocaleString(this.locale);
      }
   }

   private getCurrentLocalTime(locale: string, ianaTimeZone: string | null | undefined): string {
      if (!ianaTimeZone) {
         return '';
      }
      const timeZoneMapping: { [key: string]: string } = {
         Alaska: 'America/Anchorage', // Alaska Standard Time
         Arizona: 'America/Phoenix', // Arizona Time (No Daylight Saving)
         Atlantic: 'America/Halifax', // Atlantic Standard Time
         Central: 'America/Chicago', // Central Standard Time
         Eastern: 'America/New_York', // Eastern Standard Time
         Hawaii: 'Pacific/Honolulu', // Hawaii-Aleutian Standard Time
         Mountain: 'America/Denver', // Mountain Standard Time
         Pacific: 'America/Los_Angeles', // Pacific Standard Time
      };
      const timeZone = timeZoneMapping[ianaTimeZone] || ianaTimeZone;

      return new Date().toLocaleString(locale, { timeZone });
   }
}
